import React from 'react';
import styles from './InlineNotification.module.scss';
import Div from '../Element/Div/Div';
import BodyText from '../Typography/BodyText/BodyText';
import Flex from '../Flex/Flex';
import Icon from '../Icon/Icon';
import Gutter from '../Gutter/Gutter';
import colors from '../../shared/colors.json';
import { ArrayElementType } from '../../types/utils/ArrayElementType';
import { CreateProps } from '../../types/utils/CreateProps';
import Element, { SupportedColor } from '../Element/Element';
import { SupportedIconName } from '../../shared/icons';

export const notificationVariants = [
  'information',
  'warning',
  'success',
  'error',
] as const;

export type NotificationVarians = ArrayElementType<typeof notificationVariants>;

type InlineNotificationProps = CreateProps<
  {
    /** Title text */
    title?: string;
    /** Message text */
    messageText?: string;
    /** Function which fires on close of notification  */
    closeOnClick?: (
      event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => void;
    /** Color option of notification */
    variant: NotificationVarians;
    /** Prop to hide icon */
    hideIcon?: boolean;
    /** Start message text from the new line*/
    forceMultiline?: boolean;
    /** HTML Button */
    button?: React.ReactNode;
  },
  typeof Div,
  'boxShadow' | 'backgroundColor' | 'borderRadius' | 'padding'
>;

const InlineNotification: React.FunctionComponent<InlineNotificationProps> = ({
  title,
  messageText,
  closeOnClick,
  variant,
  hideIcon = false,
  forceMultiline = false,
  button,
  children,
  ...props
}) => {
  return (
    <Div
      boxShadow={'sm'}
      backgroundColor={mapVariant[variant].backgroundColor}
      borderRadius={{ size: 'sm' }}
      className={styles.inlineNotification}
      padding={{ size: 'sm' }}
      style={{
        borderLeft: `6px solid ${mapVariant[variant].leftBorderColor}`,
      }}
      {...props}
    >
      <Flex margin={{ top: 'xxs' }}>
        {!hideIcon && (
          <Div>
            <Icon
              name={mapVariant[variant].icon}
              color={mapVariant[variant].iconColor}
              size="sm"
              className={styles.inlineNotification_icon}
            />
          </Div>
        )}
        <Gutter size={'sm'} />
        <Flex
          wrapContent
          occupyHorizontalSpace
          directionColumn={forceMultiline}
        >
          <BodyText
            tagName="span"
            margin={{ right: 'md', bottom: 'xxs' }}
            fontWeight={'medium'}
            size={'sm'}
            color="black"
          >
            {title}
          </BodyText>
          <BodyText tagName="span" size={'sm'} color="gray700">
            {messageText}
          </BodyText>
          {children}
        </Flex>

        <Div className={styles.inlineNotification_spacer} />

        {closeOnClick && (
          <Flex>
            <Element
              onClick={closeOnClick}
              className={[
                'inline-notification-onClose-test',
                styles.inlineNotification_close,
              ]}
              tagName="button"
            >
              <Icon
                name="close"
                size="sm"
                color={'gray700'}
                className={styles.inlineNotification_icon}
              />
            </Element>
          </Flex>
        )}
      </Flex>
      {button && (
        <Flex>
          <Div className={styles.inlineNotification_spacer} />
          {button}
        </Flex>
      )}
    </Div>
  );
};

const mapVariant: {
  [key: string]: {
    icon: SupportedIconName;
    iconColor: SupportedColor;
    leftBorderColor: SupportedColor;
    backgroundColor: SupportedColor;
  };
} = {
  information: {
    icon: 'info-filled',
    iconColor: 'blue600',
    leftBorderColor: colors.blue600.color as SupportedColor,
    backgroundColor: 'blue100',
  },
  warning: {
    icon: 'alert-filled',
    iconColor: 'orange500',
    leftBorderColor: colors.orange500.color as SupportedColor,
    backgroundColor: 'orange100',
  },
  success: {
    icon: 'check-circle-filled',
    iconColor: 'green700',
    leftBorderColor: colors.green700.color as SupportedColor,
    backgroundColor: 'green100',
  },
  error: {
    icon: 'error-filled',
    iconColor: 'red600',
    leftBorderColor: colors.red600.color as SupportedColor,
    backgroundColor: 'red100',
  },
};

export default InlineNotification;
